import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Plus } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { TiptapEditor } from "@/components/TiptapEditor";
import { cn } from "@/lib/utils";
import { sendChatMessage, Message as ApiMessage } from "@/api/llmApi";

interface Message {
  id: string;
  content: string;
  sender: "user" | "ai";
}

interface ChatInterfaceProps {
  onAddToSubmission: (content: string) => void;
}

export function ChatInterface({ onAddToSubmission }: ChatInterfaceProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // Auto-scroll when messages change or when loading state changes
  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const formatMessagesForApi = (messages: Message[]): ApiMessage[] => {
    return messages.map((msg) => ({
      role: msg.sender === "user" ? "user" : "assistant",
      content: msg.content,
    }));
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue,
      sender: "user",
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      // Convert all messages to API format and add the new message
      const apiMessages = formatMessagesForApi([...messages, userMessage]);
      const response = await sendChatMessage(apiMessages);

      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        content: response,
        sender: "ai",
      };

      setMessages((prev) => [...prev, aiResponse]);
    } catch (error) {
      console.error("Failed to get AI response:", error);
      // Optionally show an error message to the user
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div 
        ref={chatContainerRef}
        className="flex-1 glass-card mb-3 rounded-lg p-3 flex items-center justify-center"
      >
        {messages.length === 0 ? (
          <div className="text-center text-white/60 py-4 text-sm">
            Start a conversation to get AI-generated writing suggestions
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-1">
            <div className="animate-pulse text-white/60 text-sm">AI is writing...</div>
          </div>
        ) : (
          // Only display the latest message
          <div
            className={cn(
              "p-3 rounded-lg w-full",
              messages[messages.length - 1].sender === "user"
                ? "bg-primary/20"
                : "bg-white/10"
            )}
          >
            <div className="text-sm font-semibold mb-1">
              {messages[messages.length - 1].sender === "user" ? "You" : "AI Assistant"}
            </div>
            <div className="whitespace-pre-wrap">{messages[messages.length - 1].content}</div>

            {messages[messages.length - 1].sender === "ai" && (
              <Button
                size="sm"
                variant="ghost"
                className="mt-2 text-sm"
                onClick={() => onAddToSubmission(messages[messages.length - 1].content)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add to Submission
              </Button>
            )}
          </div>
        )}

        {/* We can keep this as a marker for the end of messages */}
        <div ref={messagesEndRef} />
      </div>

      <div className="flex items-end gap-2">
        <Textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask for writing suggestions..."
          className="min-h-[60px] bg-card/30 resize-none text-sm"
        />
        <Button
          onClick={handleSendMessage}
          disabled={!inputValue.trim() || isLoading}
          className="bg-primary h-8"
        >
          <Send className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
